import React from "react";
import { Box, Typography, styled } from "@mui/material";
import ScrollToTopButton from "../ScrollToTopButton/ScrollToTopButton";

/**
 * The sticky bar at the bottom of the content column, shared by the storefront pages.
 *
 * It used to hold the search field plus three lines of prompt copy. The search moved to the header
 * and two of those lines became its placeholder, which leaves this as what it should have been all
 * along: one short line of positioning and whatever action the page wants beside it.
 */
const FooterContent = styled(Box)(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  zIndex: 1,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.text.primary,
  // Square. The bar meets the bottom edge of the content column, and rounding it left the page
  // background showing through the two bottom corners.
  borderRadius: 0,
  // The legal pages mount this inside a `Grid container`, where a shrink-to-fit box collapses to
  // the width of its own text instead of spanning the column.
  width: "100%",
}));

const ScrollButtonWrapper = styled(Box)({
  position: "absolute",
  top: -20,
  right: 16,
});

const Tagline = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: theme.typography.body2.fontSize,
}));

const ChildrenRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  alignItems: "center",
  flexShrink: 0,
  // Holds the action against the right edge whether or not there is a tagline on the left.
  marginLeft: "auto",
}));

interface FooterProps {
  /**
   * Rendered only when supplied. Only the landing page passes one — the legal and order pages
   * bring their own children and have no business showing homepage positioning copy.
   */
  tagline?: React.ReactNode;
  children?: React.ReactNode;
}

const Footer: React.FC<FooterProps> = ({ tagline, children }) => {
  return (
    <FooterContent>
      <ScrollButtonWrapper>
        <ScrollToTopButton />
      </ScrollButtonWrapper>
      {tagline && <Tagline>{tagline}</Tagline>}
      <ChildrenRow>{children}</ChildrenRow>
    </FooterContent>
  );
};

export default Footer;
